import { all, call, put, select, takeLatest } from 'redux-saga/effects';
import { differenceInDays } from 'date-fns';
import { push } from 'redux-first-history';

import { getAuthProps } from '@topconagriculture/auth';
import { checkPermissions } from '@topconagriculture/casbin-react';
import { Action, Resource } from '@topconagriculture/permissions';
import { hideLoader, showLoader } from '@topconagriculture/loader';

import { config } from '../../config';
import api from '../../core/api';
import { subscriptionsQueries } from '../../core/graphql/subscriptions';
import { parseDate } from '../../core/helpers/utils/dateUtils';
import { notificationError, notificationWarning } from '../../core/helpers/utils/notificationUtils';
import { errorManagementRoutesPaths } from '../../shared/ErrorManagement/ErrorManagementRoutes';
import { getFarmsListRequest } from '../entities/farms/farms.actions';
import { companyUserSelector, userSelector } from '../selectors';
import { LoadersNames } from '../ui/ui.constants';
import { getApps } from '../user/user.actions';
import { SubscriptionNotification } from './notification';
import { getClDevices, getFeedSubscriptionExpiration } from './subscription.actions';
import { SubscriptionInfo } from './subscription.types';
import { composeErrorMessage } from '../../core/helpers/utils/utils';

function* getFeedSubscriptionSaga() {
  yield put(showLoader(LoadersNames.fetchSubscriptions));
  try {
    const isCustomer = yield select(companyUserSelector.isCustomer);
    if (!isCustomer) {
      yield put(hideLoader(LoadersNames.fetchSubscriptions));
      return;
    }
    let appId = yield select(userSelector.getFeedAppId);
    if (!appId) {
      const { userId } = yield select(getAuthProps);
      const apps = yield call(api.user.getUserApps, userId);
      yield put(
        getApps.done({
          params: {},
          result: apps,
        }),
      );
      const feedApp = apps.find((app) => app.name === 'Feed');
      appId = feedApp ? feedApp.id : undefined;
    }

    if (appId) {
      const canAccessSubscriptionApi = yield call(checkPermissions, {
        resource: Resource.MARKET,
        action: Action.API_ACCESS,
      });

      if (!canAccessSubscriptionApi) {
        return;
      }

      const validSKUs = config.FEED_SUBSCRIPTION_SKUS?.split(',');
      const result: SubscriptionInfo[] = yield call(subscriptionsQueries.getSubscriptions, {
        applicationId: appId,
        skus: validSKUs,
      });
      yield put(getFeedSubscriptionExpiration.done({ result }));

      yield all(
        result.map((x) => {
          if (x?.node?.items.length > 0 && x?.node?.items?.[0].productTrial) {
            return null;
          }
          const daysToExpire = differenceInDays(parseDate(x.node.willExpireAt)!, new Date());
          if (daysToExpire < 14) {
            return put(
              notificationWarning({
                title: SubscriptionNotification(
                  x?.node?.items.length > 0 ? x?.node?.items?.[0].productName : '<unknown name>',
                  daysToExpire,
                ),
                autoDismiss: 0,
                position: 'bc',
              }),
            );
          }
          return null;
        }),
      );
    } else {
      yield put(push(errorManagementRoutesPaths.errorManagementRoot));
    }
  } catch (err) {
    yield put(push(errorManagementRoutesPaths.errorManagementRoot));
  }
  yield put(hideLoader(LoadersNames.fetchSubscriptions));
}

function* getClDevicesSaga() {
  try {
    const canReadDevices = yield call(checkPermissions, {
      resource: Resource.DEVICE,
      action: Action.READ,
    });

    if (!canReadDevices) {
      return;
    }

    const result = yield call(subscriptionsQueries.getCloudLinks);
    yield put(
      getClDevices.done({
        result,
      }),
    );
  } catch (err) {
    yield put(notificationError(composeErrorMessage(err)));
  }
}

export function* watchSubscriptionSaga() {
  yield all([
    yield takeLatest(getFeedSubscriptionExpiration.started, getFeedSubscriptionSaga),
    yield takeLatest(getFarmsListRequest.done, getFeedSubscriptionSaga),
    yield takeLatest(getFarmsListRequest.done, getClDevicesSaga),
  ]);
}
